import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FaBars,
  FaBell,
  FaChevronDown,
  FaTimes,
  FaUserCircle,
} from "react-icons/fa";
import { supabase } from "../supabaseClient";
import { signOutWithAudit } from "../utils/audit";
import { isMaintenanceOwner } from "../utils/maintenance";
import { isDeveloper } from "../utils/developer";
import { useFeatureFlags } from "../contexts/FeatureFlagsContext";
import nhsLogo from "../assets/nhs-logo.png";
import { formatProfileName } from "../utils/profileName";

const STAFF_LINKS = [
  { label: "Home", to: "/intranet/portal" },
  {
    label: "Clinical",
    menuLabel: "Clinical & patient care",
    columns: 2,
    children: [
      {
        section: "Patient records",
        label: "eCAMIS overview",
        description: "Clinical overview and patient services",
        to: "/intranet/ecamis/index",
      },
      {
        section: "Patient records",
        label: "Patient lookup",
        description: "Search patient and portal records",
        to: "/intranet/ecamis/lookup",
      },
      {
        section: "Patient records",
        label: "Create a patient",
        description: "Register a new hospital attendance",
        to: "/intranet/ecamis/create",
      },
      {
        section: "Patient records",
        label: "Appointments",
        description: "View, manage and review appointments",
        to: "/intranet/ecamis/appointments",
      },
      {
        section: "Flow & care",
        label: "Check-in queue",
        description: "Patients currently waiting to be seen",
        to: "/intranet/ecamis/check-in-queue",
      },
      {
        section: "Flow & care",
        label: "Clinical worklist",
        description: "Referrals, tests and outstanding work",
        to: "/intranet/ecamis/clinical-worklist",
      },
      {
        section: "Flow & care",
        label: "New appointment",
        description: "Create a patient appointment",
        to: "/intranet/ecamis/new-appointment",
      },
      {
        section: "Flow & care",
        label: "Wards & beds",
        description: "Bed occupancy and patient placement",
        to: "/intranet/wards",
      },
    ],
  },
  {
    label: "Work",
    menuLabel: "My working day",
    children: [
      {
        section: "Today",
        label: "Operations",
        description: "Live hospital operations and notifications",
        to: "/intranet/operations",
      },
      {
        section: "Today",
        label: "My workplace",
        description: "Availability, cover, policies, documents and appraisals",
        to: "/intranet/my-workplace",
      },
      {
        section: "Today",
        label: "Calendar",
        description: "Shifts, learning and important dates",
        to: "/intranet/calendar",
      },
      {
        section: "Today",
        label: "My schedule",
        description: "Your upcoming rota and shifts",
        to: "/intranet/myschedule",
      },
      {
        section: "Time & leave",
        label: "My timesheet",
        description: "Record and review worked time",
        to: "/intranet/timesheet",
      },
      {
        section: "Time & leave",
        label: "Leave requests",
        description: "Request and track leave",
        to: "/intranet/leave-requests",
      },
    ],
  },
  {
    label: "Learning",
    menuLabel: "Learning & compliance",
    children: [
      {
        label: "E-Learning",
        description: "Mandatory and development learning",
        to: "/intranet/e-learning",
      },
      {
        label: "Competencies",
        description: "Certifications, expiry and role requirements",
        to: "/intranet/competencies",
      },
      {
        label: "My certificates",
        description: "View, print and download recognition certificates",
        to: "/intranet/certificates",
      },
    ],
  },
  { label: "Resources", to: "/intranet/resources" },
  { label: "Help centre", to: "/intranet/help-centre" },
  {
    label: "Staff tools",
    menuLabel: "Staff tools & services",
    align: "right",
    children: [
      {
        section: "Safety & reporting",
        label: "DATIX reporting",
        description: "Report an incident or safety concern",
        to: "/intranet/datix",
      },
      {
        section: "Safety & reporting",
        label: "Complaint form",
        description: "Submit a complaint for review",
        to: "/intranet/complaint",
      },
      {
        section: "Safety & reporting",
        label: "NEWS2 calculator",
        description: "Calculate an observation score",
        to: "/intranet/news2-calculator",
      },
      {
        section: "Career & account",
        label: "Internal vacancies",
        description: "View roles available within the Trust",
        to: "/intranet/vacancies",
      },
      {
        section: "Career & account",
        label: "Profile settings",
        description: "Manage your staff profile",
        to: "/intranet/profile-settings",
      },
    ],
  },
];

const STAFF_MANAGEMENT_LINK = {
  label: "Management",
  menuLabel: "Management portal",
  columns: 2,
  align: "right",
  children: [
    {
      section: "Overview",
      label: "Management dashboard",
      description: "Trust management overview",
      to: "/intranet/management/portal",
    },
    {
      section: "Overview",
      label: "Workforce operations",
      description: "Compliance, attendance, appraisals, reports and communications",
      to: "/intranet/management/workforce",
    },
    {
      section: "People",
      label: "Staff profiles",
      description: "Accounts, positions and permissions",
      to: "/intranet/management/profiles",
    },
    {
      section: "People",
      label: "Timesheets",
      description: "Review staff time submissions",
      to: "/intranet/management/timesheet",
    },
    {
      section: "Learning",
      label: "E-Learning admin",
      description: "Create and assign staff learning",
      to: "/intranet/management/e-learning",
    },
    {
      section: "Learning",
      label: "Competency admin",
      description: "Manage certifications and compliance",
      to: "/intranet/management/competencies",
    },
    {
      section: "Learning",
      label: "Staff certificates",
      description: "Issue and manage professional staff certificates",
      to: "/intranet/management/certificates",
    },
    {
      section: "Recruitment & guidance",
      label: "Vacancies",
      description: "Manage recruitment vacancies",
      to: "/intranet/management/vacancies",
    },
    {
      section: "Recruitment & guidance",
      label: "Help Centre",
      description: "Create and maintain staff guidance",
      to: "/intranet/management/help-centre",
    },
    {
      section: "Recruitment & guidance",
      label: "File Upload",
      description: "Upload public PDF documents and copy ruht.cloud links",
      to: "/intranet/management/documents",
    },
    {
      section: "Recruitment & guidance",
      label: "Drug Matrix",
      description: "Manage medicines, categories and administration labels",
      to: "/intranet/management/drug-matrix",
    },
    {
      section: "Governance",
      label: "Complaints",
      description: "Review submitted complaints",
      to: "/intranet/management/complaints",
    },
    {
      section: "Governance",
      label: "Audit log",
      description: "Review Trust system activity",
      to: "/intranet/management/audit-log",
    },
  ],
};

const MANAGEMENT_LINKS = [
  { label: "Dashboard", to: "/intranet/management/portal" },
  {
    label: "People",
    menuLabel: "People & workforce",
    children: [
      {
        label: "Staff profiles",
        description: "Accounts, positions and permissions",
        to: "/intranet/management/profiles",
      },
      {
        label: "Timesheets",
        description: "Review staff time submissions",
        to: "/intranet/management/timesheet",
      },
    ],
  },
  { label: "Workforce", to: "/intranet/management/workforce" },
  {
    label: "Learning",
    menuLabel: "Learning & compliance",
    children: [
      {
        label: "E-Learning",
        description: "Create modules, quizzes and assignments",
        to: "/intranet/management/e-learning",
      },
      {
        label: "Competencies",
        description: "Certification and role compliance",
        to: "/intranet/management/competencies",
      },
      {
        label: "Staff certificates",
        description: "Issue, print and manage staff awards",
        to: "/intranet/management/certificates",
      },
    ],
  },
  {
    label: "Operations",
    menuLabel: "Trust operations",
    children: [
      {
        label: "Operations centre",
        description: "Live hospital operational overview",
        to: "/intranet/operations",
      },
      {
        label: "Staff intranet",
        description: "Return to the staff portal",
        to: "/intranet/portal",
      },
    ],
  },
  {
    label: "Organisation",
    menuLabel: "Organisation & governance",
    columns: 2,
    children: [
      {
        section: "Recruitment",
        label: "Vacancies",
        description: "Manage vacancies and applications",
        to: "/intranet/management/vacancies",
      },
      {
        section: "Guidance & documents",
        label: "Help Centre",
        description: "Create and maintain staff guidance",
        to: "/intranet/management/help-centre",
      },
      {
        section: "Guidance & documents",
        label: "File Upload",
        description: "Upload public PDFs and create ruht.cloud document links",
        to: "/intranet/management/documents",
      },
      {
        section: "Guidance & documents",
        label: "Drug Matrix",
        description: "Manage medicines and administration labels",
        to: "/intranet/management/drug-matrix",
      },
      {
        section: "Guidance & documents",
        label: "Email",
        description: "Management email tools",
        to: "/intranet/management/email",
      },
      {
        section: "Governance",
        label: "Complaints",
        description: "Review and manage complaints",
        to: "/intranet/management/complaints",
      },
      {
        section: "Governance",
        label: "Audit log",
        description: "Review system activity",
        to: "/intranet/management/audit-log",
      },
    ],
  },
];

const DEVELOPER_LINK = {
  label: "Developer",
  menuLabel: "Developer",
  align: "right",
  developerOnly: true,
  children: [
    {
      label: "Maintenance",
      description: "Control site-wide maintenance mode",
      to: "/intranet/management/maintenance",
    },
    {
      label: "Developer controls",
      description: "Enable or disable site pages and features",
      to: "/intranet/developer/controls",
    },
    {
      label: "AI help assistant",
      description: "Manage assistant answers and navigation",
      to: "/intranet/developer/assistant",
    },
  ],
};

function isPathActive(pathname, item) {
  if (item.to) {
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  }

  return item.children?.some(
    (child) =>
      pathname === child.to || pathname.startsWith(`${child.to}/`),
  );
}

function filterRestricted(items, owner, developer, isRouteEnabled) {
  return items
    .filter((item) => (!item.ownerOnly || owner) && (!item.developerOnly || developer))
    .map((item) => {
      if (!item.children) return item;
      return {
        ...item,
        children: item.children.filter(
          (child) =>
            (!child.ownerOnly || owner) &&
            (!child.developerOnly || developer) &&
            isRouteEnabled(child.to),
        ),
      };
    })
    .filter((item) => (!item.to || isRouteEnabled(item.to)) && (!item.children || item.children.length > 0));
}

function groupChildrenBySection(children) {
  const groups = [];
  let currentSection = null;

  children.forEach((child) => {
    const section = child.section || null;
    if (!groups.length || section !== currentSection) {
      groups.push({ section, items: [child] });
      currentSection = section;
      return;
    }
    groups[groups.length - 1].items.push(child);
  });

  return groups;
}

export default function AppHeader({
  variant = "staff",
  session: sessionProp,
  profile: profileProp,
}) {
  const [resolvedProfile, setResolvedProfile] = useState(profileProp || null);
  const [resolvedSession, setResolvedSession] = useState(sessionProp || null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const headerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isRouteEnabled } = useFeatureFlags();

  useEffect(() => {
    let active = true;

    const loadProfile = async () => {
      const session =
        sessionProp || (await supabase.auth.getSession()).data.session;
      if (!active) return;

      setResolvedSession(session || null);

      if (profileProp) {
        setResolvedProfile(profileProp);
        return;
      }

      if (!session) return;

      const { data } = await supabase
        .from("profiles")
        .select("full_name, name_prefix, roblox_name, position, management_portal")
        .eq("id", session.user.id)
        .maybeSingle();

      if (active) setResolvedProfile(data || null);
    };

    loadProfile();
    return () => {
      active = false;
    };
  }, [profileProp, sessionProp]);

  useEffect(() => {
    const userId = resolvedSession?.user?.id;
    if (!userId) {
      setUnreadNotifications(0);
      return undefined;
    }

    let active = true;
    const loadUnread = async () => {
      const { count, error } = await supabase
        .from("staff_notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .eq("is_read", false);
      if (active && !error) setUnreadNotifications(count || 0);
    };

    void loadUnread();
    const channel = supabase
      .channel(`header-notifications-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "staff_notifications",
          filter: `user_id=eq.${userId}`,
        },
        () => void loadUnread(),
      )
      .subscribe();

    return () => {
      active = false;
      supabase.removeChannel(channel);
    };
  }, [resolvedSession?.user?.id]);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const canUseManagement =
    resolvedProfile?.position === "Management" ||
    (resolvedProfile?.position === "Staff" &&
      resolvedProfile?.management_portal === true);

  const owner = isMaintenanceOwner(resolvedSession);
  const developer = isDeveloper(resolvedSession);

  const visibleLinks = useMemo(() => {
    const base = variant === "management" ? MANAGEMENT_LINKS : STAFF_LINKS;
    let nextLinks = filterRestricted(base, owner, developer, isRouteEnabled);

    if (variant === "staff" && canUseManagement) {
      const managementLink = filterRestricted([STAFF_MANAGEMENT_LINK], owner, developer, isRouteEnabled)[0];
      if (managementLink) nextLinks = [...nextLinks, managementLink];
    }

    if (developer) {
      const developerLink = filterRestricted([DEVELOPER_LINK], owner, developer, isRouteEnabled)[0];
      if (developerLink) nextLinks = [...nextLinks, developerLink];
    }

    return nextLinks;
  }, [variant, owner, developer, canUseManagement, isRouteEnabled]);

  const handleLogout = async () => {
    localStorage.removeItem("pguh:lastRoute");
    localStorage.removeItem("pguh:lastExactRoute");
    await signOutWithAudit();
    navigate("/login", { replace: true });
  };

  const toggleDropdown = (label) => {
    setOpenMenu((current) => (current === label ? null : label));
  };

  return (
    <>
      <a className="nhsuk-skip-link" href="#main-content">
        Skip to main content
      </a>

      <header className="pguh-header" ref={headerRef}>
        <div className="pguh-header__service">
          <div className="nhsuk-width-container pguh-header__service-inner">
            <NavLink to="/intranet/portal" className="pguh-brand">
              <img src={nhsLogo} alt="NHS" className="pguh-brand__logo" />
              <span className="pguh-brand__text">
                <strong>Royal University Hospitals Trust</strong>
                <small>
                  {variant === "management"
                    ? "Management portal"
                    : "Staff intranet"}
                </small>
              </span>
            </NavLink>

            <div className="pguh-header__account">
              {resolvedProfile && (
                <div className="pguh-user" aria-label="Signed-in user">
                  <FaUserCircle aria-hidden="true" />
                  <span>
                    <strong>{formatProfileName(resolvedProfile)}</strong>
                    <small>
                      {resolvedProfile.position || "User"}
                      {resolvedProfile.roblox_name
                        ? ` · @${resolvedProfile.roblox_name}`
                        : ""}
                    </small>
                  </span>
                </div>
              )}

              {resolvedSession?.user?.id && (
                <NavLink
                  to="/intranet/operations?tab=notifications"
                  className="pguh-header__notifications"
                  aria-label={`Notifications${
                    unreadNotifications ? `, ${unreadNotifications} unread` : ""
                  }`}
                >
                  <FaBell aria-hidden="true" />
                  <span className="pguh-header__notifications-label">
                    Notifications
                  </span>
                  {unreadNotifications > 0 && (
                    <strong>
                      {unreadNotifications > 99 ? "99+" : unreadNotifications}
                    </strong>
                  )}
                </NavLink>
              )}

              <button
                type="button"
                className="pguh-header__logout"
                onClick={handleLogout}
              >
                Log out
              </button>

              <button
                type="button"
                className="pguh-menu-button"
                onClick={() => setMobileOpen((open) => !open)}
                aria-expanded={mobileOpen}
                aria-controls="pguh-primary-navigation"
              >
                {mobileOpen ? <FaTimes /> : <FaBars />}
                <span>{mobileOpen ? "Close" : "Menu"}</span>
              </button>
            </div>
          </div>
        </div>

        <nav
          id="pguh-primary-navigation"
          className={`pguh-nav${mobileOpen ? " pguh-nav--open" : ""}`}
          aria-label="Primary navigation"
        >
          <div className="nhsuk-width-container pguh-nav__inner">
            {resolvedProfile && (
              <div className="pguh-nav__mobile-user">
                <FaUserCircle aria-hidden="true" />
                <span>
                  <strong>{formatProfileName(resolvedProfile)}</strong>
                  <small>{resolvedProfile.position || "User"}</small>
                </span>
              </div>
            )}

            {visibleLinks.map((item) => {
              const active = isPathActive(location.pathname, item);

              if (!item.children) {
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={() =>
                      `pguh-nav__link pguh-nav__tab${
                        active ? " pguh-nav__link--active" : ""
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              }

              const menuId = `pguh-menu-${item.label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")}`;
              const expanded = openMenu === item.label;
              const groups = groupChildrenBySection(item.children);

              return (
                <div
                  className={`pguh-nav__group${active ? " pguh-nav__group--active" : ""}`}
                  key={item.label}
                >
                  <button
                    type="button"
                    className={`pguh-nav__link pguh-nav__button pguh-nav__tab${
                      active ? " pguh-nav__link--active" : ""
                    }`}
                    onClick={() => toggleDropdown(item.label)}
                    aria-expanded={expanded}
                    aria-controls={menuId}
                  >
                    <span>{item.label}</span>
                    <FaChevronDown aria-hidden="true" />
                  </button>

                  <div
                    id={menuId}
                    className={`pguh-nav__dropdown${
                      expanded ? " pguh-nav__dropdown--open" : ""
                    }${item.align === "right" ? " pguh-nav__dropdown--right" : ""}${
                      item.columns === 2 ? " pguh-nav__dropdown--wide" : ""
                    }`}
                  >
                    <div className="pguh-nav__dropdown-heading">
                      {item.menuLabel || item.label}
                    </div>
                    <div
                      className={`pguh-nav__dropdown-content${
                        item.columns === 2
                          ? " pguh-nav__dropdown-content--columns"
                          : ""
                      }`}
                    >
                      {groups.map((group, groupIndex) => (
                        <div
                          className="pguh-nav__dropdown-section"
                          key={`${item.label}-${group.section || groupIndex}`}
                        >
                          {group.section && (
                            <div className="pguh-nav__dropdown-section-title">
                              {group.section}
                            </div>
                          )}
                          {group.items.map((child) => {
                            const childActive =
                              location.pathname === child.to ||
                              location.pathname.startsWith(`${child.to}/`);

                            return (
                              <NavLink
                                key={child.to}
                                to={child.to}
                                className={() =>
                                  `pguh-nav__dropdown-link${
                                    childActive
                                      ? " pguh-nav__dropdown-link--active"
                                      : ""
                                  }`
                                }
                              >
                                <span>{child.label}</span>
                                {child.description && (
                                  <small>{child.description}</small>
                                )}
                              </NavLink>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              className="pguh-nav__mobile-logout"
              onClick={handleLogout}
            >
              Log out
            </button>
          </div>
        </nav>
      </header>
      <span id="main-content" className="pguh-main-anchor" tabIndex="-1" />
    </>
  );
}
